/**
 * ECS Client Logging Utilities
 * -----------------------------------------------------------------------------
 * Provides structured logging functionality for ECS client operations.
 * Includes both ECS client logger factory and general logging utilities.
 */

import type { ILogger, ILoggerFactory } from "@1js/ecs-client";
import { logDev, infoDev, warnDev, errorDev, debugDev } from '@/logging/devConsole';

/**
 * Console Logger implementation for ECS client.
 */
class ConsoleLogger implements ILogger {
  constructor(private readonly label: string) {}

  log(message: string, ...args: unknown[]): void {
    logDev(`[${this.label}] ${message}`, ...args);
  }

  warn(message: string, ...args: unknown[]): void {
    warnDev(`[${this.label}] ${message}`, ...args);
  }

  error(message: string, ...args: unknown[]): void {
    errorDev(`[${this.label}] ${message}`, ...args);
  }

  debug(message: string, ...args: unknown[]): void {
    debugDev(`[${this.label}] ${message}`, ...args);
  }
}

/**
 * Logger Factory implementation for ECS client.
 */
export class ConsoleLoggerFactory implements ILoggerFactory {
  newLogger(label: string, _loggerName: string): ILogger {
    return new ConsoleLogger(label);
  }
}

/**
 * Structured logging levels.
 */
export enum LogLevel {
  DEBUG = 0,
  INFO = 1,
  WARN = 2,
  ERROR = 3,
}

/**
 * Current log level - can be configured.
 */
let currentLogLevel: LogLevel = LogLevel.INFO;

/**
 * Set the current log level.
 */
export function setLogLevel(level: LogLevel): void {
  currentLogLevel = level;
}

/**
 * Get the current log level.
 */
export function getLogLevel(): LogLevel {
  return currentLogLevel;
}

/**
 * Check if a log level should be output.
 */
function shouldLog(level: LogLevel): boolean {
  return level >= currentLogLevel;
}

/**
 * Format log message with timestamp and context.
 */
function formatMessage(level: string, message: string, context?: string): string {
  const timestamp = new Date().toISOString();
  const contextStr = context ? ` [${context}]` : '';
  return `${timestamp} [ECS-${level}]${contextStr} ${message}`;
}

/**
 * Debug level logging.
 */
export function logDebug(message: string, context?: string, ...args: any[]): void {
  if (shouldLog(LogLevel.DEBUG)) {
    debugDev(formatMessage('DEBUG', message, context), ...args);
  }
}

/**
 * Info level logging.
 */
export function logInfo(message: string, context?: string, ...args: any[]): void {
  if (shouldLog(LogLevel.INFO)) {
    infoDev(formatMessage('INFO', message, context), ...args);
  }
}

/**
 * Warning level logging.
 */
export function logWarn(message: string, context?: string, ...args: any[]): void {
  if (shouldLog(LogLevel.WARN)) {
    warnDev(formatMessage('WARN', message, context), ...args);
  }
}

/**
 * Error level logging.
 */
export function logError(message: string, error?: any, context?: string, ...args: any[]): void {
  if (shouldLog(LogLevel.ERROR)) {
    const errorStr = error ? ` Error: ${error.message || error}` : '';
    errorDev(formatMessage('ERROR', message + errorStr, context), error, ...args);
  }
}

/**
 * Log ECS operation start.
 */
export function logOperationStart(operation: string, details?: any): void {
  logInfo(`Starting ${operation}`, 'OPERATION', details);
}

/**
 * Log ECS operation success.
 */
export function logOperationSuccess(operation: string, duration?: number, details?: any): void {
  const durationStr = duration !== undefined ? ` (${duration}ms)` : '';
  logInfo(`Completed ${operation}${durationStr}`, 'OPERATION', details);
}

/**
 * Log ECS operation failure.
 */
export function logOperationFailure(operation: string, error: any, duration?: number): void {
  const durationStr = duration !== undefined ? ` (${duration}ms)` : '';
  logError(`Failed ${operation}${durationStr}`, error, 'OPERATION');
}

/**
 * Log configuration change.
 */
export function logConfigChange(changes: Array<{flag: string, oldValue: any, newValue: any}>): void {
  const changeCount = changes.filter(c => c.oldValue !== c.newValue).length;
  if (changeCount > 0) {
    logInfo(`Configuration updated: ${changeCount} flags changed`, 'CONFIG', changes);
  } else {
    logDebug('Configuration checked: no changes detected', 'CONFIG');
  }
}

/**
 * Log user context information.
 */
export function logUserContext(hasAccount: boolean, tenantId?: string, userId?: string): void {
  if (hasAccount) {
    const maskedTenantId = tenantId ? `${tenantId.substring(0, 8)}...` : 'unknown';
    const maskedUserId = userId ? `${userId.substring(0, 8)}...` : 'unknown';
    logInfo(`User context: tenant ${maskedTenantId}, user ${maskedUserId}`, 'USER');
  } else {
    logInfo('No user context available', 'USER');
  }
}

/**
 * Log ECS client state for debugging.
 */
export function logClientState(state: {
  hasClient: boolean;
  hasConfig: boolean;
  hasUserAccount: boolean;
  fetchScheduleActive: boolean;
  listenerCount: number;
}): void {
  logDebug('ECS Client State', 'STATE', state);
}